import React, { useEffect } from 'react';

import { useRouter } from 'next/router.js';
import PropTypes from 'prop-types';

import { updateFiltersInUrlParams } from '~/gridv1_1/utils/filters.js';
import { QueryParamBehavior } from '~/gridv1_1/utils/routing';
import { useToastContext } from '~/shared/components/Toast/ToastContext';
import useColorActions from '~/shared/utils/useColorActions.js';
import { FormattedMessage } from '~/techstyle-shared/react-intl/index.js';

import { CUSTOM_COLOR_FILTER } from '../../constants.js';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';

const ColorBox = ({ color }) => (
  <div
    style={{
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      border: 'none',
      position: 'relative',
      top: '3px',
      marginRight: '4px',
      backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})`,
      display: 'inline-block',
    }}
  />
);

const colorShape = PropTypes.shape({
  r: PropTypes.number.isRequired,
  g: PropTypes.number.isRequired,
  b: PropTypes.number.isRequired,
});

ColorBox.propTypes = { color: colorShape.isRequired };

const ColorWheelFilterItem = ({ color }) => (
  <>
    <ColorBox color={color} />
    <FormattedMessage
      defaultMessage="Custom Color"
      id="site_product_grid.custom_color_filter_label"
    />
  </>
);

ColorWheelFilterItem.propTypes = { color: colorShape.isRequired };

/**
 * Initializes custom color filter from URL on page load.
 * This component is always mounted (not inside the drawer) to ensure
 * color filters are applied immediately when the page loads with custom_color in URL.
 */
function MobileGridCustomColorInitializer() {
  const { customFacetFilters, setCustomFacetFilters } =
    useUnifiedProductGridData();
  const colorActions = useColorActions();
  const router = useRouter();
  const { showMessage } = useToastContext();

  useEffect(() => {
    const query = router.asPath.split('?')[1];
    const urlParams = new URLSearchParams(query);
    const facetValue = urlParams.get(CUSTOM_COLOR_FILTER.name);

    if (facetValue) {
      const existingColor = customFacetFilters[CUSTOM_COLOR_FILTER.name];

      if (existingColor?.hasError) {
        showMessage(
          <FormattedMessage
            id="site_product_grid.custom_color_filter_error"
            defaultMessage="No matches. Please try another color."
          />,
          undefined,
          true
        );
        updateFiltersInUrlParams({
          facetName: CUSTOM_COLOR_FILTER.name,
          facetOption: [],
          router,
          queryParamBehavior: QueryParamBehavior.REPLACE,
        });
        return;
      }

      const existingColorValue =
        existingColor && !existingColor.hasError
          ? Object.values(existingColor.label?.props?.color || {}).join(',')
          : null;

      // Only apply if URL color is different from the applied color
      const shouldApply = existingColorValue !== facetValue;

      if (shouldApply) {
        const [r, g, b] = facetValue.split(',').map((v) => parseInt(v, 10));
        const selectedColor = { r, g, b };

        // Fetch color tags and apply to customFacetFilters
        colorActions
          .getNearestColorTagsByColorValues(selectedColor)
          .then((nearestColors) => {
            const payload = nearestColors?.payload;
            if (!payload) {
              return;
            }

            const tagIdList = payload.map((c) => c.tagId);

            setCustomFacetFilters((prev) => ({
              ...prev,
              [CUSTOM_COLOR_FILTER.name]: {
                ...CUSTOM_COLOR_FILTER,
                label: <ColorWheelFilterItem color={selectedColor} />,
                filters: { tag_id_list: tagIdList },
                hasError: false,
              },
            }));
          });
      }
    } else if (customFacetFilters[CUSTOM_COLOR_FILTER.name]) {
      // URL no longer has color but customFacets does - remove it
      setCustomFacetFilters((prev) => {
        const { [CUSTOM_COLOR_FILTER.name]: _, ...rest } = prev;
        return rest;
      });
    }
  }, [
    router,
    customFacetFilters,
    colorActions,
    setCustomFacetFilters,
    showMessage,
  ]);

  return null;
}

export default MobileGridCustomColorInitializer;
